import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

const capabilities = [
  "Threat detection",
  "Infrastructure security",
  "Access control",
  "Continuous monitoring",
];

const easeOut = [0.22, 1, 0.36, 1];

export default function Security() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "-15% 0px -15% 0px" }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="security"
      ref={sectionRef}
      data-testid="security-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center py-28 md:py-32"
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0.28, mixBlendMode: "lighten" }}
      >
        <source
          src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/uswh0qbu_VID-20260419-WA0000.mp4"
          type="video/mp4"
        />
      </video>

      {/* Darker vignette for text contrast, no hard edge */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(5,8,12,0.25) 0%, rgba(5,8,12,0.65) 70%, rgba(5,8,12,0.85) 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-8 text-center">
        {/* System Label */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: inView ? 1 : 0 }}
          transition={{ duration: 1.2, delay: 0.1, ease: easeOut }}
          className="sys-label text-center mb-10 md:mb-14"
        >
          SYS.SECURE // ACTIVE
        </motion.p>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 24 }}
          transition={{ duration: 1.2, delay: 0.4, ease: easeOut }}
          className="security-heading pop-click font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tighter text-white mb-5"
        >
          <span className="showcase-accent bg-gradient-to-r from-[#00E5FF] via-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent inline-block">Security</span>{" "}
          that stays invisible.
        </motion.h2>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 18 }}
          transition={{ duration: 1.2, delay: 0.9, ease: easeOut }}
          className="pop-click font-body text-base md:text-lg text-zinc-300/85 font-light max-w-lg mx-auto"
        >
          Designed to protect without slowing you down.
        </motion.p>

        {/* Capability list — staggered reveal */}
        <ul className="mt-14 md:mt-16 flex flex-col items-center gap-3 md:gap-3.5">
          {capabilities.map((cap, i) => (
            <motion.li
              key={cap}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 16 }}
              transition={{
                duration: 1.1,
                delay: 1.3 + i * 0.35,
                ease: easeOut,
              }}
              className="font-body text-sm md:text-base text-zinc-400 font-light tracking-wide pop-click"
            >
              {cap}
            </motion.li>
          ))}
        </ul>

        {/* Accent hairline */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: inView ? 0.6 : 0, scaleX: inView ? 1 : 0 }}
          transition={{ duration: 1.2, delay: 2.9, ease: easeOut }}
          className="mx-auto mt-12 md:mt-14 h-px w-16"
          style={{
            backgroundColor: "rgba(0, 229, 255, 0.45)",
            transformOrigin: "center",
          }}
        />

        {/* Bottom line */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: inView ? 1 : 0 }}
          transition={{ duration: 1.2, delay: 3.2, ease: easeOut }}
          className="mt-6 font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-zinc-500"
        >
          Always active. Always watching.
        </motion.p>
      </div>
    </section>
  );
}
